import React, { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faLock } from '@fortawesome/free-solid-svg-icons';
import { usePopup } from "../context/PopupContext";
import { changePassword } from "../auth/AuthService";

// CSS for a simple circular spinner
const spinnerStyles = `
  .spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 3px solid #fff;
    border-top: 3px solid transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ChangePassword = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showPopup } = usePopup();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      showPopup("New passwords do not match", "error");
      return;
    }
    
    // Check password requirements
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      showPopup("Password must be at least 8 characters, contain one uppercase letter, one special character, and a mix of numbers & alphabets", "error");
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the API to change password
      const response = await changePassword(currentPassword, newPassword);
      
      if (response.success) {
        // Show success message
        showPopup("Password changed successfully!", "success");
        
        // Close the modal
        if (onClose) onClose();
      } else {
        showPopup(response.message || "Failed to change password", "error");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showPopup("Failed to change password. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-gray-800/20 backdrop-blur-md p-6" style={{
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      backgroundColor: 'rgba(55, 65, 81, 0.2)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)'
    }}>
      <style>{spinnerStyles}</style>
      
      <h2 className="text-xl font-semibold text-white flex items-center mb-4">
        <FontAwesomeIcon icon={faLock} className="mr-2 text-gray-300" />
        <span>Change Password</span>
      </h2>
      
      <form onSubmit={handleChangePassword}>
        {/* Current Password */}
        <div className="mb-4">
          <label className="block text-sm text-gray-300 mb-1">Current Password</label>
          <div className="relative">
            <input
              type={showCurrentPassword ? "text" : "password"}
              className="w-full p-3 bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-400 border-0 pr-10"
              style={{
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(55, 65, 81, 0.3)'
              }}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-3 text-gray-300 hover:text-white"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            >
              <FontAwesomeIcon icon={showCurrentPassword ? faEye : faEyeSlash} />
            </button>
          </div>
        </div>
        
        {/* New Password */}
        <div className="mb-4">
          <label className="block text-sm text-gray-300 mb-1">New Password</label>
          <div className="relative">
            <input
              type={showNewPassword ? "text" : "password"}
              className="w-full p-3 bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-400 border-0 pr-10"
              style={{
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(55, 65, 81, 0.3)'
              }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-3 text-gray-300 hover:text-white"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              <FontAwesomeIcon icon={showNewPassword ? faEye : faEyeSlash} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Must be at least 8 characters, contain one uppercase letter, one special character, and a mix of numbers & alphabets.
          </p>
        </div>
        
        {/* Confirm New Password */}
        <div className="mb-6">
          <label className="block text-sm text-gray-300 mb-1">Confirm New Password</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full p-3 bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-400 border-0 pr-10"
              style={{
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(55, 65, 81, 0.3)'
              }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-3 text-gray-300 hover:text-white"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
            </button>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition disabled:bg-blue-400 flex items-center justify-center"
            disabled={loading}
          >
            {loading && <span className="spinner"></span>}
            {loading ? "Changing..." : "Change Password"}
          </button>
          
          <button
            type="button"
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;